import { useState } from "react";
import { loginSuperAdmin } from "../services/authService";
import { showSuccess, showError } from "../utils/toast";
import { useNavigate } from "react-router-dom";
import logo from "../assets/image/eLaba_logo.png";
import { Eye, EyeOff } from "lucide-react";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError(""); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const res = await loginSuperAdmin(form);
      
      // Check if login was successful based on backend response
      if (res.data.success) {
        const storage = rememberMe ? localStorage : sessionStorage;
        const otherStorage = rememberMe ? sessionStorage : localStorage;

        otherStorage.removeItem('superAdminData');
        otherStorage.removeItem('isAuthenticated');
        storage.setItem('superAdminData', JSON.stringify(res.data.super_admin));
        storage.setItem('isAuthenticated', 'true');
        showSuccess(`Welcome back, ${res.data.super_admin.username}!`);
        navigate("/dashboard");
      } else {
        const errorMsg = res.data.message || "Login failed";
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Invalid credentials";
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
  <style>{`
        .elaba-auth-root {
          position: relative;
          overflow-x: hidden;
        }
        .elaba-auth-divider {
          position: absolute;
          left: 50%;
          top: 7%;
          transform: translateX(-50%);
          width: 2px;
          height: 86%;
          background: linear-gradient(to bottom, #e3e9f0 0%, #b3d0f7 100%);
          box-shadow: 0 0 12px #b3d0f7aa;
          border-radius: 2px;
          z-index: 3;
          opacity: 0.7;
        }
        @media (max-width: 900px) {
          .elaba-auth-divider {
            display: none !important;
          }
          .elaba-auth-root {
            min-height: 100dvh !important;
          }
          .elaba-auth-left {
            padding: 40px 24px !important;
          }
          .elaba-auth-left h1 {
            font-size: clamp(2rem, 6vw, 2.6rem) !important;
            line-height: 1.12 !important;
          }
          .elaba-auth-left p {
            font-size: 1.05rem !important;
            max-width: 32rem !important;
          }
          .elaba-auth-right {
            padding: 40px 20px !important;
          }
          .elaba-auth-right > div {
            max-width: 28rem !important;
            width: min(100%, 28rem) !important;
          }
        }
        @media (max-width: 900px) {
          .elaba-auth-root {
            flex-direction: column !important;
          }
          .elaba-auth-left, .elaba-auth-right {
            flex: none !important;
            width: 100% !important;
            min-height: 220px !important;
            padding: 32px 10px !important;
            border-radius: 0 !important;
          }
          .elaba-auth-left {
            min-height: 180px !important;
            padding-bottom: 0 !important;
          }
          .elaba-auth-right {
            min-height: 320px !important;
            padding-top: 0 !important;
          }
        }
        @media (max-width: 600px) {
          .elaba-auth-root {
            padding: 0 !important;
            min-height: 100dvh !important;
          }
          .elaba-auth-left {
            min-height: 42vh !important;
            padding: 32px 18px 24px !important;
          }
          .elaba-auth-right {
            min-height: auto !important;
            padding: 24px 14px 40px !important;
          }
          .elaba-auth-left h1 {
            font-size: clamp(1.75rem, 8vw, 2.1rem) !important;
          }
          .elaba-auth-left img {
            width: 58px !important;
            height: 58px !important;
            margin-bottom: 14px !important;
          }
          .elaba-auth-left p {
            font-size: 0.98rem !important;
            max-width: 100% !important;
          }
          .elaba-auth-right > div {
            padding: 24px 18px !important;
            max-width: min(100%, 100vw) !important;
            border-radius: 18px !important;
          }
          .elaba-auth-right h2 {
            font-size: 22px !important;
            margin-bottom: 22px !important;
          }
          .elaba-auth-right input,
          .elaba-auth-right button {
            font-size: 15px !important;
          }
        }
        .elaba-fade-in {
          animation: fadeInUp 0.8s cubic-bezier(.39,.575,.565,1.000) both;
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: none; }
        }
        .elaba-glow {
          box-shadow: 0 0 0 4px #00c6ff33, 0 8px 32px 0 #007bff33;
        }
        .elaba-avatar {
          width: 70px; height: 70px; border-radius: 50%; background: linear-gradient(135deg, #007bff 60%, #00c6ff 100%); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px auto; box-shadow: 0 2px 12px #00c6ff33;
        }
        .elaba-avatar svg {
          width: 38px; height: 38px; color: #fff;
        }
        .elaba-loading-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(248, 250, 252, 0.72);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        .elaba-loading-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          padding: 28px 32px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(179, 208, 247, 0.6);
          box-shadow: 0 18px 50px rgba(0, 123, 255, 0.16);
          min-width: min(90vw, 260px);
        }
        .elaba-loading-spinner {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 4px solid rgba(0, 123, 255, 0.12);
          border-top-color: #007bff;
          border-right-color: #00c6ff;
          animation: elabaSpin 0.9s linear infinite;
        }
        .elaba-loading-text {
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          letter-spacing: 0.2px;
        }
        @keyframes elabaSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {loading && (
        <div className="elaba-loading-overlay" role="status" aria-live="polite" aria-label="Logging in">
          <div className="elaba-loading-card">
            <div className="elaba-loading-spinner" />
            <div className="elaba-loading-text">Logging in...</div>
          </div>
        </div>
      )}
      <div className="elaba-auth-root" style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
        fontFamily: 'Segoe UI, sans-serif',
        padding: '0',
        position: 'relative',
      }}>
        <div className="elaba-auth-divider" />
        {/* Left Creative Side */}
        <div className="elaba-auth-left elaba-fade-in" style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          padding: '56px 48px',
          background: 'linear-gradient(135deg, #007bff 60%, #00c6ff 100%)',
          position: 'relative',
          overflow: 'hidden',
          minWidth: 0,
        }}>
          <div style={{
            zIndex: 2,
            textAlign: 'center',
          }}>
            <img src={logo} alt="eLaba Logo" style={{
              width: '70px',
              height: '70px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto 18px auto',
              borderRadius: '16px',
              boxShadow: '0 2px 12px #00c6ff33',
              background: '#f8fafc',
            }} />
            <h1 style={{ fontSize: '2.8rem', fontWeight: 700, marginBottom: '20px', letterSpacing: '1px', textShadow: '0 2px 12px #007bff55' }}>
              Welcome to <span style={{ color: '#ffe082' }}>eLaba</span> Super Admin
            </h1>
            <p style={{ fontSize: '1.2rem', maxWidth: '400px', margin: '0 auto', opacity: 0.95, textShadow: '0 1px 8px #007bff33' }}>
              Manage your platform, users, and services with powerful tools and a beautiful dashboard.
            </p>
          </div>
          {/* Decorative Circles */}
          <div style={{
            position: 'absolute',
            top: '-80px',
            left: '-80px',
            width: '200px',
            height: '200px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '50%',
            zIndex: 1,
            filter: 'blur(1px)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-60px',
            right: '-60px',
            width: '140px',
            height: '140px',
            background: 'rgba(255,255,255,0.12)',
            borderRadius: '50%',
            zIndex: 1,
            filter: 'blur(1px)',
          }} />
        </div>

        {/* Right Login Form Side */}
        <div className="elaba-auth-right elaba-fade-in" style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f8fafc',
          padding: 'clamp(32px, 5vw, 56px) 24px',
          minWidth: 0,
        }}>
          <div className="elaba-glow" style={{
            background: 'white',
            padding: 'clamp(24px, 4vw, 44px) clamp(18px, 3vw, 32px) clamp(22px, 3vw, 36px)',
            borderRadius: '22px',
            boxShadow: '0 8px 32px 0 #b3d0f733, 0 1.5px 8px #007bff22',
            width: '100%',
            maxWidth: 'min(100%, 420px)',
            border: '1.5px solid #e3e9f0',
            transition: 'box-shadow 0.3s',
            position: 'relative',
          }}>
            <h2 style={{
              textAlign: 'center',
              marginBottom: '30px',
              color: '#007bff',
              fontSize: 'clamp(22px, 4.2vw, 26px)',

              fontWeight: 700,
              letterSpacing: '0.5px',
              textShadow: '0 1px 8px #00c6ff22',
            }}>
              Super Admin Login
            </h2>

            {error && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: 500,
                boxShadow: '0 2px 8px #f8d7da33',
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off">
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '14px 14px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    background: '#f9fbfd',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#007bff'; e.target.style.boxShadow = '0 0 0 2px #007bff33'; }}
                  onBlur={e => { e.target.style.borderColor = '#ddd'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div style={{ marginBottom: '20px', position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '14px 48px 14px 14px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    background: '#f9fbfd',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#007bff'; e.target.style.boxShadow = '0 0 0 2px #007bff33'; }}
                  onBlur={e => { e.target.style.borderColor = '#ddd'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                    border: 'none',
                    background: 'transparent',
                    color: '#64748b',
                    cursor: 'pointer',
                  }}
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                margin: '-4px 0 20px',
                color: '#475569',
                fontSize: '14px',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: '#007bff',
                    cursor: 'pointer',
                  }}
                />
                Remember me
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? '#ccc' : 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px',
                  borderRadius: '8px',
                    fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  transition: 'background 0.3s',
                  marginBottom: '8px',
                  letterSpacing: '0.5px',
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>

          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
